import { ApiMethodKeys, EntityName, LocalStorageKeys, SnackbarKeys, notificationsMsgs } from "@sso/constant";
import { closeDialog } from "@sso/store/slices/dialog";
import { pushSnackbar } from "@sso/store/slices/snackbar";
import { ApiPageResponse, ID, Licence, LicenceFormType, LicenceSummary } from "@sso/types";
import { localStorageHelper } from "@sso/utils";

import { ssoApi } from "..";
import { CacheKeys } from "../cacheKeys";
import { Endpoints } from "../endpoints";

const createDownloadMutation = (endpoint: string) => ({
  queryFn: async (licenceId: ID) => {
    try {
      const token = localStorageHelper.getItem<string>(LocalStorageKeys.TOKEN);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}${Endpoints.LICENCES}/${endpoint}?licenceId=${licenceId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-ClientId": process.env.REACT_APP_CLIENT_ID || "",
            "X-Security-Token": token || "",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `licence-${licenceId}.basemaplicence`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      // Convert response to blob
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { data: undefined };
    } catch (error: any) {
      return { error: { status: "FETCH_ERROR", error: error.message } };
    }
  },
});

export const licencesApi = ssoApi
  .enhanceEndpoints({
    addTagTypes: [
      CacheKeys.LICENCES,
      CacheKeys.USERS,
      CacheKeys.USER_ACCESSED_PRODUCTS,
      CacheKeys.USER_PRODUCTS,
      CacheKeys.API_ACCOUNTS,
      CacheKeys.SOFTWARE_LICENCES,
      CacheKeys.PRODUCTS,
    ],
  })
  .injectEndpoints({
    endpoints: builder => ({
      licences: builder.query<ApiPageResponse<LicenceSummary>, string>({
        query: query => ({ url: `${Endpoints.LICENCES}?${query}` }),
        providesTags: [CacheKeys.LICENCES],
      }),
      addLicence: builder.mutation<Licence, LicenceFormType>({
        query: data => ({ url: `${Endpoints.LICENCES}`, method: ApiMethodKeys.POST, data }),
        invalidatesTags: [
          CacheKeys.LICENCES,
          CacheKeys.USERS,
          CacheKeys.USER_ACCESSED_PRODUCTS,
          CacheKeys.USER_PRODUCTS,
          CacheKeys.API_ACCOUNTS,
          CacheKeys.SOFTWARE_LICENCES,
          CacheKeys.PRODUCTS,
        ],
        onQueryStarted(_, { dispatch, queryFulfilled }) {
          queryFulfilled.then(() => {
            dispatch(
              pushSnackbar({ type: SnackbarKeys.SUCCESS, title: notificationsMsgs.ADD_ITEM(EntityName.LICENCE) }),
            );
            dispatch(closeDialog());
          });
        },
      }),
      editLicence: builder.mutation<Licence, LicenceFormType>({
        query: data => ({ url: `${Endpoints.LICENCES}`, method: ApiMethodKeys.PUT, data }),
        invalidatesTags: [
          CacheKeys.LICENCES,
          CacheKeys.USERS,
          CacheKeys.USER_ACCESSED_PRODUCTS,
          CacheKeys.USER_PRODUCTS,
          CacheKeys.API_ACCOUNTS,
          CacheKeys.SOFTWARE_LICENCES,
          CacheKeys.PRODUCTS,
        ],
        onQueryStarted(_, { dispatch, queryFulfilled }) {
          queryFulfilled.then(() => {
            dispatch(
              pushSnackbar({ type: SnackbarKeys.SUCCESS, title: notificationsMsgs.Edit_ITEM(EntityName.LICENCE) }),
            );
            dispatch(closeDialog());
          });
        },
      }),
      deleteLicence: builder.mutation<void, string>({
        query: licenceId => ({ url: Endpoints.LICENCES, params: { licenceId }, method: ApiMethodKeys.DELETE }),
        invalidatesTags: [CacheKeys.LICENCES, CacheKeys.API_ACCOUNTS],
        onQueryStarted(_, { dispatch, queryFulfilled }) {
          queryFulfilled.then(() => {
            dispatch(
              pushSnackbar({ type: SnackbarKeys.SUCCESS, title: notificationsMsgs.DELETE_ITEM(EntityName.LICENCE) }),
            );
            dispatch(closeDialog());
          });
        },
      }),
      getLicenceById: builder.query<Licence, ID>({
        query: licenceId => ({ url: `${Endpoints.LICENCES}/${licenceId}` }),
        providesTags: [CacheKeys.LICENCES],
        transformResponse: (response: { result: Licence }) => {
          return response.result;
        },
      }),
      downloadTraccClientLicence: builder.mutation<void, ID>(createDownloadMutation("Download-Client-Licence")),
      downloadTraccServerLicence: builder.mutation<void, ID>(createDownloadMutation("Download-Server-Licence")),
    }),
  });

export const {
  useLicencesQuery,
  useAddLicenceMutation,
  useEditLicenceMutation,
  useDeleteLicenceMutation,
  useGetLicenceByIdQuery,
  useDownloadTraccClientLicenceMutation,
  useDownloadTraccServerLicenceMutation,
} = licencesApi;
